import { MapState } from '@carto/kepler.gl'
import { updateMap } from '@carto/kepler.gl/actions'
import { AppStore } from 'app/state/types'
import { xyToLngLat, lngLatToXY } from 'features/builder/utils/mapUtils'
import { MapCommentAnchor } from 'features/maps/types'

import { MAP_POPOVER_WIDTH } from '../common/MapPopover'
import { PANEL_WIDTH as RIGHT_SLIDER_PANEL_WIDTH } from '../MapPopover/constants'
import { COMMENT_MARKER_SIZE } from 'features/collaboration/ui/comments/CommentMarker'
import { MIN_COMMENT_POPOVER_HEIGHT } from './MapCommentPopover'

// offset need to center MapCommentPopover with marker on map with right slider panel open
const MARKER_X_OFFSET_DESKTOP = MAP_POPOVER_WIDTH + RIGHT_SLIDER_PANEL_WIDTH
const MARKER_X_OFFSET_MOBILE = MAP_POPOVER_WIDTH

export function centerMapOnComment(
  anchor: MapCommentAnchor,
  { store, isMobile }: { store: AppStore; isMobile: boolean }
) {
  const mapState = store.getState().keplerGl.map?.mapState
  const controlsPaneOpen = store.getState().builder.controlsPaneOpen
  const markerLocation = getMarkerLonLat(
    {
      ...mapState,
      zoom: anchor.zoom
    },
    {
      lng: anchor.longitude,
      lat: anchor.latitude
    },
    isMobile ? MARKER_X_OFFSET_MOBILE : controlsPaneOpen ? MAP_POPOVER_WIDTH : MARKER_X_OFFSET_DESKTOP
  )
  if (markerLocation) {
    store.dispatch(
      updateMap({
        latitude: markerLocation.lat,
        longitude: markerLocation.lng,
        ...(anchor.zoom && { zoom: anchor.zoom })
      })
    )
  }
}

export function ensureCommentPopoverVisible(
  anchor: MapCommentAnchor,
  { store, isMobile }: { store: AppStore; isMobile: boolean }
) {
  const mapState = store.getState().keplerGl.map?.mapState
  const controlsPaneOpen = store.getState().builder.controlsPaneOpen
  const markerLocation = getMapCenterToFitPopover(
    mapState,
    {
      lng: anchor.longitude,
      lat: anchor.latitude
    },
    (isMobile || controlsPaneOpen ? COMMENT_MARKER_SIZE : MAP_POPOVER_WIDTH) + COMMENT_MARKER_SIZE,
    MIN_COMMENT_POPOVER_HEIGHT
  )
  if (markerLocation) {
    store.dispatch(
      updateMap({
        latitude: markerLocation.lat,
        longitude: markerLocation.lng
      })
    )
  }
}

/**
 * Get the marker coordinates with an horizontal offset, so whole control (marker + popover) feels
 * centered in map viewport.
 */
export const getMarkerLonLat = (
  mapState: MapState,
  { lng, lat }: { lng: number; lat: number },
  xOffsetLeft: number
): { lng: number; lat: number } | null => {
  const xy = lngLatToXY(mapState, [lng, lat])
  if (!xy) return null

  const { x, y } = xy
  const xWithOffset = x + xOffsetLeft / 2
  return xyToLngLat(mapState, xWithOffset, y)
}

/**
 * Get new map lat/lon, so marker is visible in viewport.
 *
 * * mapState - current map state
 * * lat, lng - expected popover anchor in map
 * * controlWidth/controlHeight - size of control to check if marker would be outside of
 *     viewport
 */
export const getMapCenterToFitPopover = (
  mapState: MapState,
  { lng, lat }: { lng: number; lat: number },
  controlWidth: number,
  controlHeight: number
): { lng: number; lat: number } | null => {
  const xy = lngLatToXY(mapState, [lng, lat])
  if (!xy) return null
  const { x, y } = xy
  let newX = x
  if (x < 0) {
    newX = 0
  } else if (x > mapState.width - controlWidth) {
    newX = mapState.width - controlWidth
  }

  let newY = y
  if (y < controlHeight) {
    newY = controlHeight
  } else if (y > mapState.height - controlHeight) {
    newY = mapState.height - controlHeight
  }

  const newCenterX = mapState.width / 2 - (newX - x)
  const newCenterY = mapState.height / 2 - (newY - y)

  return xyToLngLat(mapState, newCenterX, newCenterY)
}
